import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import "./UsersPage.css";
import { API_BASE } from "../config";

const ROLE_OPTIONS = [
  { value: "segretario", label: "Segretario" },
  { value: "tesoriere", label: "Tesoriere" },
  { value: "aiuto_segretario", label: "Aiuto Segretario" },
  { value: "aiuto_tesoriere", label: "Aiuto Tesoriere" },
];

/* ===================== Helpers ===================== */

/** ✅ Ruoli robusti (supporta user.roles array + fallback user.role singolo) */
function normalizeRoles(user) {
  const roles = Array.isArray(user?.roles) ? user.roles.slice() : [];
  if (roles.length === 0 && user?.role) roles.push(user.role);
  return roles.map((r) => String(r).trim().toLowerCase()).filter(Boolean);
}

function hasAnyRole(user, allowed) {
  const roles = normalizeRoles(user);
  return allowed.some((r) => roles.includes(String(r).toLowerCase()));
}

async function readPayloadSafe(res) {
  if (res.status === 204) return null;
  const contentType = res.headers.get("content-type") || "";
  try {
    if (contentType.includes("application/json")) return await res.json();
    const text = await res.text();
    return text ? { message: text } : null;
  } catch {
    return null;
  }
}

function getApiErrorMessage(res, payload) {
  const msg =
    (payload && typeof payload === "object" && (payload.error || payload.message)) ||
    (typeof payload === "string" && payload) ||
    null;

  if (res.status === 401) return msg || "Non autorizzato. Effettua il login.";
  if (res.status === 403) return msg || "Permesso negato.";
  if (res.status === 404) return msg || "Endpoint non trovato (404).";
  return msg || `Errore HTTP ${res.status}`;
}

function Icon({ children, label }) {
  return (
    <span className="icon" aria-hidden="true" title={label}>
      {children}
    </span>
  );
}

function Tooltip({ text, children }) {
  return (
    <span className="tip" data-tip={text}>
      {children}
    </span>
  );
}

/* ===================== Component ===================== */

export default function UsersPage({ token, user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // create state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "segretario",
  });
  const [showPassword, setShowPassword] = useState(false);

  // edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    username: "",
    role: "segretario",
    password: "",
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  // delete state
  const [deletingId, setDeletingId] = useState(null);

  // abort controller per fetch lista
  const abortRef = useRef(null);

  const isDeveloper = useMemo(() => hasAnyRole(user, ["developer"]), [user]);

  // ✅ gestione utenti: tipicamente admin o developer
  const canManageUsers = useMemo(() => hasAnyRole(user, ["admin", "developer"]), [user]);
  const canCreateUsers = canManageUsers;

  const roleLabelByValue = useMemo(() => {
    const map = {};
    for (const r of ROLE_OPTIONS) map[r.value] = r.label;
    return map;
  }, []);

  const resetAlerts = useCallback(() => {
    setSuccess("");
    setError("");
    setCreateError("");
    setEditError("");
  }, []);

  const fetchUsers = useCallback(
    async (signal) => {
      if (!token) return;

      // annulla chiamata precedente
      abortRef.current?.abort?.();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError("");

      try {
        const res = await fetch(`${API_BASE}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: signal || controller.signal,
        });

        const payload = await readPayloadSafe(res);
        if (!res.ok) throw new Error(getApiErrorMessage(res, payload));

        setUsers(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setError(err?.message || "Errore nel recupero degli utenti");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => {
      controller.abort();
      abortRef.current?.abort?.();
    };
  }, [token, fetchUsers]);

  const handleRefresh = () => {
    resetAlerts();
    setEditingId(null);
    fetchUsers();
  };

  /* -------------------- CREATE -------------------- */

  const onChange = (key) => (e) => {
    resetAlerts();
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!token || !canCreateUsers) return;

    const username = String(form.username || "").trim();
    const password = String(form.password || "");
    const role = String(form.role || "").trim();

    if (!username || !password || !role) {
      setCreateError("Compila tutti i campi richiesti.");
      return;
    }

    // se non developer, vincoliamo al tenant dell’utente (se presente)
    const tenantId = user?.tenantId;
    const body = isDeveloper ? { username, password, role } : { username, password, role, tenantId };

    setCreating(true);
    resetAlerts();

    try {
      const res = await fetch(`${API_BASE}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(getApiErrorMessage(res, payload));

      setForm({ username: "", password: "", role: "segretario" });
      setShowPassword(false);
      setSuccess("Utente creato con successo.");
      await fetchUsers();
    } catch (err) {
      setCreateError(err?.message || "Errore nella creazione dell'utente");
    } finally {
      setCreating(false);
    }
  };

  /* -------------------- EDIT -------------------- */

  const startEdit = (u) => {
    resetAlerts();
    setEditingId(u.id);
    setShowEditPassword(false);
    setEditForm({
      username: u.username ?? "",
      role: u.role ?? "segretario",
      password: "", // opzionale
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError("");
    setShowEditPassword(false);
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!token || !editingId || !canManageUsers) return;

    const username = String(editForm.username || "").trim();
    const role = String(editForm.role || "").trim();

    if (!username || !role) {
      setEditError("Username e ruolo sono obbligatori.");
      return;
    }

    setSaving(true);
    setEditError("");
    setSuccess("");

    try {
      const body = { username, role };
      if (editForm.password && String(editForm.password).trim()) {
        body.password = String(editForm.password).trim();
      }

      const res = await fetch(`${API_BASE}/api/users/${editingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(getApiErrorMessage(res, payload));

      setSuccess("Utente modificato con successo.");
      setEditingId(null);
      setShowEditPassword(false);
      setEditForm({ username: "", role: "segretario", password: "" });
      await fetchUsers();
    } catch (err) {
      setEditError(err?.message || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  /* -------------------- DELETE -------------------- */

  const deleteUser = async (u) => {
    if (!token || !canManageUsers || !u?.id) return;

    const ok = window.confirm(`Confermi l'eliminazione dell'utente "${u.username}"?`);
    if (!ok) return;

    setDeletingId(u.id);
    resetAlerts();

    try {
      const res = await fetch(`${API_BASE}/api/users/${u.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = await readPayloadSafe(res);
      if (!res.ok) throw new Error(getApiErrorMessage(res, payload));

      setSuccess("Utente eliminato con successo.");
      if (editingId === u.id) setEditingId(null);
      await fetchUsers();
    } catch (err) {
      setError(err?.message || "Errore durante l'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="users-page">
      <div className="users-page__header">
        <div>
          <h2 className="users-page__title">Gestione Utenti</h2>
          <div className="users-page__subtitle">
            {isDeveloper ? "Vista developer • tutti i tenant" : `Tenant: ${user?.tenantId ?? "—"}`}
          </div>
        </div>

        <button
          type="button"
          className="btn btn--ghost"
          onClick={handleRefresh}
          disabled={!token || loading}
          title={!token ? "Effettua il login per vedere gli utenti" : "Aggiorna elenco utenti"}
        >
          <Icon label="Aggiorna">🔄</Icon>
          {loading ? "Aggiorno…" : "Aggiorna"}
        </button>
      </div>

      {!token && (
        <div className="users-page__alert users-page__alert--info" role="status">
          Per visualizzare gli utenti effettua l’accesso.
        </div>
      )}

      {!canManageUsers && token && (
        <div className="users-page__alert users-page__alert--warning" role="status">
          Non hai i permessi per creare/modificare/eliminare utenti.
        </div>
      )}

      {createError && (
        <div className="users-page__alert users-page__alert--error" role="alert">
          {createError}
        </div>
      )}
      {success && (
        <div className="users-page__alert users-page__alert--success" role="status">
          {success}
        </div>
      )}
      {error && (
        <div className="users-page__alert users-page__alert--error" role="alert">
          {error}
        </div>
      )}

      {/* CREATE */}
      {canCreateUsers && (
        <section className="users-page__card">
          <h3 className="users-page__subtitle">Crea nuovo utente</h3>

          <form onSubmit={handleCreate} className="users-page__form">
            <div className="users-page__field">
              <label className="users-page__label" htmlFor="new-username">
                Username
              </label>
              <input
                id="new-username"
                type="text"
                autoComplete="username"
                placeholder="es. mario.rossi"
                value={form.username}
                onChange={onChange("username")}
                required
                disabled={creating}
                className="users-page__input"
              />
            </div>

            <div className="users-page__field">
              <label className="users-page__label" htmlFor="new-password">
                Password
              </label>

              <div className="users-page__password">
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Inserisci una password"
                  value={form.password}
                  onChange={onChange("password")}
                  required
                  disabled={creating}
                  className="users-page__input"
                />

                <Tooltip text={showPassword ? "Nascondi password" : "Mostra password"}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setShowPassword((s) => !s)}
                    disabled={creating}
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  >
                    <Icon label="Mostra/Nascondi">👁️</Icon>
                    {showPassword ? "Nascondi" : "Mostra"}
                  </button>
                </Tooltip>
              </div>
            </div>

            <div className="users-page__field">
              <label className="users-page__label" htmlFor="new-role">
                Ruolo
              </label>
              <select
                id="new-role"
                value={form.role}
                onChange={onChange("role")}
                disabled={creating}
                className="users-page__select"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {!isDeveloper && (
                <div className="users-page__hint">
                  L’utente verrà creato nel tenant <b>{user?.tenantId ?? "—"}</b>.
                </div>
              )}
            </div>

            <div className="users-page__actions">
              <button type="submit" className="btn btn--primary" disabled={creating}>
                <Icon label="Crea">➕</Icon>
                {creating ? "Creazione in corso…" : "Crea utente"}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* LIST */}
      <section className="users-page__card">
        <div className="users-page__listHeader">
          <h3 className="users-page__subtitle">Elenco utenti</h3>
          <span className="users-page__count" aria-label={`Totale utenti: ${users.length}`}>
            {users.length} totali
          </span>
        </div>

        {loading ? (
          <p>Caricamento…</p>
        ) : users.length === 0 ? (
          <p>Nessun utente trovato.</p>
        ) : (
          <div className="users-page__tableWrap">
            <table className="transaction-table users-page__table">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Username</th>
                  <th scope="col">Ruolo</th>
                  {isDeveloper && <th scope="col">Tenant ID</th>}
                  {canManageUsers && (
                    <th scope="col" style={{ width: 260 }}>
                      Azioni
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {users.map((u) => {
                  const isEditing = editingId === u.id;

                  return (
                    <tr key={u.id ?? `${u.username}-${u.tenantId ?? "na"}`}>
                      <td>{u.id}</td>

                      <td>
                        {isEditing ? (
                          <input
                            className="users-page__input"
                            value={editForm.username}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, username: e.target.value }))
                            }
                            disabled={saving}
                          />
                        ) : (
                          u.username
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <select
                            className="users-page__select"
                            value={editForm.role}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, role: e.target.value }))
                            }
                            disabled={saving}
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          roleLabelByValue[u.role] ?? u.role
                        )}
                      </td>

                      {isDeveloper && <td>{u.tenantId ?? "—"}</td>}

                      {canManageUsers && (
                        <td>
                          {isEditing ? (
                            <div className="users-actions users-actions--edit">
                              {editError && (
                                <div
                                  className="users-page__alert users-page__alert--error"
                                  role="alert"
                                >
                                  {editError}
                                </div>
                              )}

                              <div className="users-page__password">
                                <input
                                  className="users-page__input"
                                  type={showEditPassword ? "text" : "password"}
                                  autoComplete="new-password"
                                  placeholder="Nuova password (opzionale)"
                                  value={editForm.password}
                                  onChange={(e) =>
                                    setEditForm((p) => ({ ...p, password: e.target.value }))
                                  }
                                  disabled={saving}
                                />

                                <Tooltip
                                  text={showEditPassword ? "Nascondi password" : "Mostra password"}
                                >
                                  <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => setShowEditPassword((s) => !s)}
                                    disabled={saving}
                                  >
                                    <Icon label="Mostra/Nascondi">👁️</Icon>
                                    {showEditPassword ? "Nascondi" : "Mostra"}
                                  </button>
                                </Tooltip>
                              </div>

                              <div className="users-actions__row">
                                <Tooltip text="Salva modifiche">
                                  <button
                                    type="button"
                                    className="btn btn--primary btn--sm"
                                    onClick={saveEdit}
                                    disabled={saving}
                                  >
                                    <Icon label="Salva">💾</Icon>
                                    {saving ? "Salvo…" : "Salva"}
                                  </button>
                                </Tooltip>

                                <Tooltip text="Annulla modifica">
                                  <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={cancelEdit}
                                    disabled={saving}
                                  >
                                    <Icon label="Annulla">✖</Icon>
                                    Annulla
                                  </button>
                                </Tooltip>
                              </div>
                            </div>
                          ) : (
                            <div className="users-actions">
                              <Tooltip text="Modifica utente">
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  onClick={() => startEdit(u)}
                                >
                                  <Icon label="Modifica">✏️</Icon>
                                  Modifica
                                </button>
                              </Tooltip>

                              <Tooltip text="Elimina utente">
                                <button
                                  type="button"
                                  className="btn btn--danger btn--sm"
                                  onClick={() => deleteUser(u)}
                                  disabled={deletingId === u.id}
                                >
                                  <Icon label="Elimina">🗑️</Icon>
                                  {deletingId === u.id ? "Elimino…" : "Elimina"}
                                </button>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
